import { useState, useEffect } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * UI state management store
 *
 * This comprehensive store manages all UI-related state including:
 * - Theme and appearance settings
 * - Navigation and routing state
 * - Search and filtering functionality
 * - Recent items and user history
 * - Modal and dialog management
 * - Loading states and progress indicators
 * - Notifications and alerts
 *
 * The store provides a centralized way to manage UI state across
 * the entire application, ensuring consistent behavior and
 * persistent user preferences.
 */

/**
 * Theme and appearance state interface
 *
 * Manages application theme settings including light/dark mode
 * and system preference detection.
 */
interface ThemeState {
  /** Current theme setting */
  theme: 'light' | 'dark' | 'system';
  /** Set the theme preference */
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
}

/**
 * Navigation and routing state interface
 *
 * Manages navigation-related UI state including sidebar state,
 * active routes, and breadcrumb navigation.
 */
interface NavigationState {
  /** Whether the sidebar is collapsed */
  sidebarCollapsed: boolean;
  /** Set sidebar collapsed state */
  setSidebarCollapsed: (collapsed: boolean) => void;
  /** Currently active route path */
  activeRoute: string;
  /** Set the active route */
  setActiveRoute: (route: string) => void;
  /** Breadcrumb navigation items */
  breadcrumbs: Array<{ label: string; href?: string }>;
  /** Set breadcrumb navigation */
  setBreadcrumbs: (
    breadcrumbs: Array<{ label: string; href?: string }>
  ) => void;
  /** Set of expanded folder IDs in the sidebar */
  expandedFolders: Set<string>;
  /** Toggle folder expanded state */
  toggleFolderExpanded: (folderId: string) => void;
  /** Set folder expanded state */
  setFolderExpanded: (folderId: string, expanded: boolean) => void;
  /** Expand multiple folders at once */
  expandFolders: (folderIds: string[]) => void;
  /** Expand all folders */
  expandAllFolders: (folderIds: string[]) => void;
  /** Collapse all folders */
  collapseAllFolders: () => void;
}

/**
 * Search and filtering state interface
 *
 * Manages search functionality and filter states across the application.
 * Provides centralized search state for consistent behavior.
 */
interface SearchState {
  /** Current search query string */
  searchQuery: string;
  /** Set the search query */
  setSearchQuery: (query: string) => void;
  /**
   * Active search filters - intentionally flexible Record type
   * to support various filter structures across different features
   */
  searchFilters: Record<string, unknown>;
  /** Set search filters */
  setSearchFilters: (filters: Record<string, unknown>) => void;
  /** Clear all search state */
  clearSearch: () => void;
}

/**
 * Recent items and history state interface
 *
 * Tracks user's recent activity using IDs only (not full server data).
 * Server data should be fetched via TanStack Query using these IDs.
 */
interface RecentState {
  /** Recently accessed table IDs (max 5) */
  recentTableIds: string[];
  /** Set recent table IDs list */
  setRecentTableIds: (ids: string[]) => void;
  /** Add a table ID to recent list */
  addRecentTableId: (id: string) => void;
  /** Recently accessed linked table IDs (max 10) */
  recentLinkedTableIds: string[];
  /** Set recent linked table IDs list */
  setRecentLinkedTableIds: (ids: string[]) => void;
  /** Add a linked table ID to recent list */
  addRecentLinkedTableId: (id: string) => void;
  /** Clear all recent items */
  clearRecentItems: () => void;
}

/**
 * Modal and dialog management state interface
 *
 * Provides centralized modal state management for consistent
 * modal behavior across the application.
 */
interface ModalState {
  /** Map of modal IDs to their open/closed state */
  openModals: Record<string, boolean>;
  /**
   * Map of modal IDs to their associated data - intentionally flexible
   * Record type to support various modal data structures
   */
  modalData: Record<string, unknown>;
  /** Open a modal with optional data */
  openModal: (modalId: string, data?: unknown) => void;
  /** Close a specific modal */
  closeModal: (modalId: string) => void;
  /** Close all open modals */
  closeAllModals: () => void;
  /** Check if a modal is open */
  isModalOpen: (modalId: string) => boolean;
}

/**
 * Loading and progress states interface
 *
 * Manages loading states for various operations and provides
 * granular control over loading indicators.
 */
interface LoadingState {
  /** Global loading state */
  globalLoading: boolean;
  /** Map of operation keys to their loading states */
  loadingStates: Record<string, boolean>;
  /** Set global loading state */
  setGlobalLoading: (loading: boolean) => void;
  /** Set loading state for a specific operation */
  setLoading: (key: string, loading: boolean) => void;
  /** Check if a specific operation is loading */
  isLoading: (key: string) => boolean;
  /** Clear all loading states */
  clearLoadingStates: () => void;
}

/**
 * Workspace preferences state interface
 *
 * Manages user preferences for the workspace view,
 * persisted to localStorage for a consistent experience.
 */
interface WorkspacePreferencesState {
  /** View mode for workspace items (grid or list) */
  workspaceViewMode: 'grid' | 'list';
  /** Set the workspace view mode */
  setWorkspaceViewMode: (mode: 'grid' | 'list') => void;
  /** Column widths for list view (in pixels) */
  listViewColumnWidths: {
    name: number;
    updated_at: number;
    created_at: number;
    size: number;
    type: number;
    tags: number;
    status: number;
  };
  /** Set column width for a specific column */
  setColumnWidth: (column: keyof WorkspacePreferencesState['listViewColumnWidths'], width: number) => void;
  /** Reset column widths to defaults */
  resetColumnWidths: () => void;
}

/**
 * Notification and alerts state interface
 *
 * Manages application notifications and alerts with support
 * for different notification types and read/unread states.
 */
interface NotificationState {
  /** Array of notification objects */
  notifications: Array<{
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message?: string;
    timestamp: number;
    read: boolean;
  }>;
  /** Add a new notification */
  addNotification: (
    notification: Omit<
      NotificationState['notifications'][0],
      'id' | 'timestamp' | 'read'
    >
  ) => void;
  /** Mark a notification as read */
  markAsRead: (notificationId: string) => void;
  /** Mark all notifications as read */
  markAllAsRead: () => void;
  /** Remove a specific notification */
  removeNotification: (notificationId: string) => void;
  /** Clear all notifications */
  clearNotifications: () => void;
}

/**
 * Combined UI state interface
 *
 * Merges all UI state interfaces into a single comprehensive store
 * that provides complete UI state management capabilities.
 */
interface UIState
  extends ThemeState,
    NavigationState,
    SearchState,
    RecentState,
    ModalState,
    LoadingState,
    WorkspacePreferencesState,
    NotificationState {
  // ===== GLOBAL UI UTILITY ACTIONS =====
  /** Reset all UI state to defaults */
  resetUI: () => void;
  /** Export current UI state for debugging/backup */
  exportUIState: () => Record<string, unknown>;
}

/**
 * UI store implementation
 *
 * Creates a comprehensive Zustand store for all UI-related state.
 * The store manages theme, navigation, search, recent items, modals,
 * loading states, and notifications with fresh state on every page load.
 *
 * Key features:
 * - User preferences management (theme, sidebar state, recent items)
 * - Centralized modal management
 * - Granular loading state control
 * - Rich notification system
 * - Search and filtering state
 * - Recent items tracking
 * - Fresh state on every page load (no persistence)
 */
export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
  // ===== THEME STATE =====
  /** Current theme setting (defaults to system preference) */
  theme: 'system',
  /** Set the theme preference */
  setTheme: (theme) => set({ theme }),

  // ===== NAVIGATION STATE =====
  /** Whether the sidebar is collapsed (defaults to expanded) */
  sidebarCollapsed: false,
  /** Set sidebar collapsed state */
  setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),

  /** Currently active route path */
  activeRoute: '/',
  /** Set the active route */
  setActiveRoute: (activeRoute) => set({ activeRoute }),

  /** Breadcrumb navigation items */
  breadcrumbs: [],
  /** Set breadcrumb navigation */
  setBreadcrumbs: (breadcrumbs) => set({ breadcrumbs }),

  /** Set of expanded folder IDs in the sidebar */
  expandedFolders: new Set<string>(),
  /** Toggle folder expanded state */
  toggleFolderExpanded: (folderId) =>
    set((state) => {
      const newExpanded = new Set(state.expandedFolders);
      if (newExpanded.has(folderId)) {
        newExpanded.delete(folderId);
      } else {
        newExpanded.add(folderId);
      }
      return { expandedFolders: newExpanded };
    }),
  /** Set folder expanded state */
  setFolderExpanded: (folderId, expanded) =>
    set((state) => {
      const newExpanded = new Set(state.expandedFolders);
      if (expanded) {
        newExpanded.add(folderId);
      } else {
        newExpanded.delete(folderId);
      }
      return { expandedFolders: newExpanded };
    }),
  /** Expand multiple folders at once */
  expandFolders: (folderIds) =>
    set((state) => {
      const newExpanded = new Set(state.expandedFolders);
      folderIds.forEach((id) => newExpanded.add(id));
      return { expandedFolders: newExpanded };
    }),
  /** Expand all folders */
  expandAllFolders: (folderIds) =>
    set({ expandedFolders: new Set(folderIds) }),
  /** Collapse all folders */
  collapseAllFolders: () =>
    set({ expandedFolders: new Set<string>() }),

  // ===== SEARCH STATE =====
  /** Current search query string */
  searchQuery: '',
  /** Set the search query */
  setSearchQuery: (searchQuery) => set({ searchQuery }),

  /** Active search filters */
  searchFilters: {},
  /** Set search filters */
  setSearchFilters: (searchFilters) => set({ searchFilters }),

  /** Clear all search state */
  clearSearch: () => set({ searchQuery: '', searchFilters: {} }),

  // ===== RECENT ITEMS STATE =====
  /** Recently accessed table IDs */
  recentTableIds: [],
  /** Set recent table IDs list */
  setRecentTableIds: (recentTableIds) => set({ recentTableIds }),

  /**
   * Add a table ID to recent list
   *
   * Removes existing entry if present and adds to front.
   * Maintains a maximum of 5 recent table IDs.
   */
  addRecentTableId: (id) => {
    const current = get().recentTableIds;
    const filtered = current.filter((existingId) => existingId !== id);
    set({ recentTableIds: [id, ...filtered].slice(0, 5) });
  },

  /** Recently accessed linked table IDs */
  recentLinkedTableIds: [],
  /** Set recent linked table IDs list */
  setRecentLinkedTableIds: (recentLinkedTableIds) => set({ recentLinkedTableIds }),

  /**
   * Add a linked table ID to recent list
   *
   * Removes existing entry if present and adds to front.
   * Maintains a maximum of 10 recent linked table IDs.
   */
  addRecentLinkedTableId: (id) => {
    const current = get().recentLinkedTableIds;
    const filtered = current.filter((existingId) => existingId !== id);
    set({ recentLinkedTableIds: [id, ...filtered].slice(0, 10) });
  },

  /** Clear all recent items */
  clearRecentItems: () => set({ recentTableIds: [], recentLinkedTableIds: [] }),

  // ===== MODAL STATE =====
  /** Map of modal IDs to their open/closed state */
  openModals: {},
  /** Map of modal IDs to their associated data */
  modalData: {},

  /**
   * Open a modal with optional data
   *
   * @param modalId Unique identifier for the modal
   * @param data Optional data to associate with the modal
   */
  openModal: (modalId, data) =>
    set((state) => ({
      openModals: { ...state.openModals, [modalId]: true },
      modalData: data
        ? { ...state.modalData, [modalId]: data }
        : state.modalData,
    })),

  /**
   * Close a specific modal and clear its data
   *
   * @param modalId Unique identifier for the modal
   */
  closeModal: (modalId) =>
    set((state) => {
      const { [modalId]: _, ...restModals } = state.openModals;
      const { [modalId]: __, ...restData } = state.modalData;
      return {
        openModals: restModals,
        modalData: restData,
      };
    }),

  /** Close all open modals and clear all modal data */
  closeAllModals: () => set({ openModals: {}, modalData: {} }),

  /**
   * Check if a modal is currently open
   *
   * @param modalId Unique identifier for the modal
   * @returns True if the modal is open, false otherwise
   */
  isModalOpen: (modalId) => {
    const { openModals } = get();
    return Boolean(openModals[modalId]);
  },

  // ===== LOADING STATE =====
  /** Global loading state */
  globalLoading: false,
  /** Map of operation keys to their loading states */
  loadingStates: {},

  /** Set global loading state */
  setGlobalLoading: (globalLoading) => set({ globalLoading }),

  /**
   * Set loading state for a specific operation
   *
   * @param key Unique identifier for the operation
   * @param loading Loading state for the operation
   */
  setLoading: (key, loading) =>
    set((state) => ({
      loadingStates: { ...state.loadingStates, [key]: loading },
    })),

  /**
   * Check if a specific operation is loading
   *
   * @param key Unique identifier for the operation
   * @returns True if the operation is loading, false otherwise
   */
  isLoading: (key) => {
    const { loadingStates } = get();
    return Boolean(loadingStates[key]);
  },

  /** Clear all loading states */
  clearLoadingStates: () => set({ loadingStates: {} }),

  // ===== WORKSPACE PREFERENCES STATE =====
  /** View mode for workspace items (defaults to grid) */
  workspaceViewMode: 'grid',
  /** Set the workspace view mode */
  setWorkspaceViewMode: (workspaceViewMode) => set({ workspaceViewMode }),

  /** Default column widths for list view */
  listViewColumnWidths: {
    name: 300,
    updated_at: 180,
    created_at: 180,
    size: 80,
    type: 100,
    tags: 70,
    status: 70,
  },
  /** Set column width for a specific column */
  setColumnWidth: (column, width) =>
    set((state) => ({
      listViewColumnWidths: {
        ...state.listViewColumnWidths,
        [column]: Math.max(60, width), // Minimum width of 60px
      },
    })),
  /** Reset column widths to defaults */
  resetColumnWidths: () =>
    set({
      listViewColumnWidths: {
        name: 300,
        updated_at: 180,
        created_at: 180,
        size: 80,
        type: 100,
        tags: 70,
        status: 70,
      },
    }),

  // ===== NOTIFICATION STATE =====
  /** Array of notification objects */
  notifications: [],

  /**
   * Add a new notification
   *
   * Creates a unique ID and timestamp, maintains max of 50 notifications.
   *
   * @param notification Notification data (without id, timestamp, read)
   */
  addNotification: (notification) => {
    const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newNotification = {
      ...notification,
      id,
      timestamp: Date.now(),
      read: false,
    };
    set((state) => ({
      notifications: [newNotification, ...state.notifications].slice(0, 50),
    }));
  },

  /**
   * Mark a notification as read
   *
   * @param notificationId Unique identifier for the notification
   */
  markAsRead: (notificationId) => {
    set((state) => ({
      notifications: state.notifications.map((notification) =>
        notification.id === notificationId
          ? { ...notification, read: true }
          : notification
      ),
    }));
  },

  /** Mark all notifications as read */
  markAllAsRead: () => {
    set((state) => ({
      notifications: state.notifications.map((notification) => ({
        ...notification,
        read: true,
      })),
    }));
  },

  /**
   * Remove a specific notification
   *
   * @param notificationId Unique identifier for the notification
   */
  removeNotification: (notificationId) => {
    set((state) => ({
      notifications: state.notifications.filter(
        (notification) => notification.id !== notificationId
      ),
    }));
  },

  /** Clear all notifications */
  clearNotifications: () => set({ notifications: [] }),

  // ===== GLOBAL UI UTILITY ACTIONS =====

  /**
   * Reset all UI state to defaults
   *
   * Clears all temporary state while preserving user preferences
   * like theme and sidebar state.
   */
  resetUI: () =>
    set({
      sidebarCollapsed: false,
      activeRoute: '/',
      breadcrumbs: [],
      expandedFolders: new Set<string>(),
      searchQuery: '',
      searchFilters: {},
      recentTableIds: [],
      recentLinkedTableIds: [],
      openModals: {},
      modalData: {},
      globalLoading: false,
      loadingStates: {},
      notifications: [],
    }),

  /**
   * Export current UI state for debugging/backup
   *
   * Returns a subset of the current state that's useful for
   * debugging or creating backups of user preferences.
   *
   * @returns Object containing key UI state for export
   */
  exportUIState: () => {
    const state = get();
    return {
      theme: state.theme,
      sidebarCollapsed: state.sidebarCollapsed,
      recentTableIds: state.recentTableIds,
      recentLinkedTableIds: state.recentLinkedTableIds,
      workspaceViewMode: state.workspaceViewMode,
    };
  },
    }),
    {
      name: 'loada-ui-preferences',
      // Use localStorage with explicit JSON storage for better compatibility
      storage: createJSONStorage(() => localStorage),
      // Only persist user preferences, not transient state
      partialize: (state) => ({
        theme: state.theme,
        workspaceViewMode: state.workspaceViewMode,
        listViewColumnWidths: state.listViewColumnWidths,
      }),
    }
  )
);

/**
 * Hook to check if the UI store has been hydrated from localStorage
 *
 * Use this to avoid hydration mismatches when using persisted state.
 * Returns false during SSR and initial render, true after hydration.
 */
export const useUIStoreHydrated = () => {
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    // Check if already hydrated
    const unsubFinishHydration = useUIStore.persist.onFinishHydration(() => {
      setHydrated(true);
    });

    // If already hydrated (happens on subsequent renders)
    setHydrated(useUIStore.persist.hasHydrated());

    return () => {
      unsubFinishHydration();
    };
  }, []);

  return hydrated;
};
